import { useCallback, useEffect, useState } from "react";

import API_SERVICES from "@services/index";

export const useOnlineStatus = (
  config = {
    interval: 10000,
    timeout: 10000,
  }
) => {
  const { interval, timeout } = config;

  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isChecking, setIsChecking] = useState(false);

  const checkOnlineStatus = useCallback(() => {
    if (!navigator.onLine) {
      setIsOnline(false);
      return;
    }

    setIsChecking(true);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    API_SERVICES.KIOSK.CHECK_CONNECTIVITY(controller.signal)
      .then(response => {
        clearTimeout(timeoutId);
        setIsOnline(response.ok);
      })
      .catch(() => {
        setIsOnline(false);
      })
      .finally(() => {
        setIsChecking(false);
      });
  }, [timeout]);

  useEffect(() => {
    checkOnlineStatus();

    const intervalId = setInterval(checkOnlineStatus, interval);

    const handleOnline = () => checkOnlineStatus();
    const handleOffline = () => {
      setIsOnline(false);
      setIsChecking(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkOnlineStatus();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [checkOnlineStatus, interval]);

  return { isChecking, isOnline };
};
